import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import getKhattabatStore from ".";
import { isPositiveNumber, reduce } from "et3-type-guards";
import { getKhattabatResultsStore } from "./results-store";
import { TSelectionPages } from "../../Types";
import { getSpecificDateWithDays } from "../../core/et3-date";
import { apiCallGetKhattabasRemunerations } from "../../api/khattbat-api/api-call-get-khattabats-remunerations";
import { TKhattabatAdminsRemunerations } from "../../TypesNEW";
import getCorePopUpStore from "../core/core-popup-store";
import getCoreUIStore from "../core/core-ui-store";

class KhattabatSelectsStore {
  selectedConversations = observable.box<{
    [userID in number]: boolean;
  }>({});

  remunerationCycleFrom = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 5, daysAdded: 0 })
  );

  remunerationCycleTo = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );

  khattabatAdminsRenumerations = observable.box<TKhattabatAdminsRemunerations>(
    []
  );

  setKhattabatAdminsRemunerations = (
    remunerations: TKhattabatAdminsRemunerations
  ) => {
    runInAction(() => {
      this.khattabatAdminsRenumerations.set(remunerations);
    });
  };

  getMarkedUserIds = () => {
    const initialIds = Object.keys(this.selectedConversations.get());
    let filteredInitialIds: number[] = [];
    initialIds.forEach((id) => {
      if (this.selectedConversations.get()[parseInt(id, 10)]) {
        filteredInitialIds.push(parseInt(id, 10));
      }
    });
    return filteredInitialIds;
  };

  toggleAllMarksStates = (pageType: TSelectionPages) => {
    if (this.isSelectedAll(pageType)) {
      this.initializeAndClearMarksStateWithClients(pageType);
    } else {
      this.selectAllClients(pageType);
    }
  };

  markUserConversation = (userID: number, state: boolean) => {
    runInAction(() => {
      this.selectedConversations.get()[userID] = state;
    });
  };

  targetedUserIds = (pageType: TSelectionPages) => {
    switch (pageType) {
      case "clientPage":
        return getKhattabatStore().getCurrentUserIds();
      case "matchResultPage":
        return getKhattabatResultsStore().getCurrentMatchIds();
      default:
        return [];
    }
  };

  selectAllClients = (pageType: TSelectionPages) => {
    let currentUsersId = this.targetedUserIds(pageType);
    this.initializeAndClearMarksStateWithClients(pageType);
    currentUsersId.forEach((id) => {
      this.markUserConversation(id, true);
    });
  };

  initializeAndClearMarksStateWithClients = (pageType: TSelectionPages) => {
    this.resetSelectedConversations();
    runInAction(() => {
      switch (pageType) {
        case "clientPage":
          return getKhattabatStore()
            .clients.get()
            .forEach((client) => {
              this.markUserConversation(client.userID, false);
            });
        case "matchResultPage":
          const stringMatchesIds = Object.keys(
            getKhattabatResultsStore().matchesConversations.get()
          );
          return stringMatchesIds.forEach((stringMatchId) => {
            const matchId = parseInt(stringMatchId, 10);
            this.markUserConversation(matchId, false);
          });
      }
    });
  };

  isMarkedConversation = ({ userID }: { userID: number }) => {
    return reduce(() => this.selectedConversations.get()[userID], false);
  };

  selectedCount = computed(() => {
    return this.getMarkedUserIds().length;
  });

  toogleMarkConversation = ({ userID }: { userID: number }) => {
    const oldState = reduce(() => this.isMarkedConversation({ userID }), false);
    runInAction(() => {
      this.selectedConversations.get()[userID] = !oldState;
    });
  };

  isSelectedAll = (pageType: TSelectionPages) => {
    let currentUsersIdLength = this.targetedUserIds(pageType).length;
    return (
      currentUsersIdLength === this.selectedCount.get() &&
      isPositiveNumber(currentUsersIdLength)
    );
  };

  resetSelectedConversations = () => {
    runInAction(() => {
      this.selectedConversations.set({});
    });
  };

  getKhattabatRemunerationsDatabase = async () => {
    getCoreUIStore().setIsLoading("KHATTABAT_REMUNERATION", true);
    const { data, code, status } = await apiCallGetKhattabasRemunerations({
      remunerationCycleFrom: this.remunerationCycleFrom.get(),
      remunerationCycleTo: this.remunerationCycleTo.get()
    });
    getCoreUIStore().setIsLoading("KHATTABAT_REMUNERATION", false);

    if (status === "OK") {
      this.setKhattabatAdminsRemunerations(data);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };
}

export const getKhattabatSelectsStore = memoize(
  () => {
    return new KhattabatSelectsStore();
  },
  () => 1
);

export default getKhattabatSelectsStore;
